import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Users, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const MemberSelect = ({ members, selectedMember, onSelect, placeholder = "All Members" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Get current member name for display
    const currentMemberName = selectedMember
        ? members?.find(m => String(m.Id) === String(selectedMember))?.MemberName
        : placeholder;

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (memberId) => {
        onSelect(memberId);
        setIsOpen(false);
    };

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all group ${isOpen ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}`}
            >
                <div className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-3 bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 overflow-hidden">
                        <Users className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors shrink-0" />
                        <span className="truncate">
                            {currentMemberName}
                        </span>
                    </div>
                </div>
                <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-500' : ''}`} />
                </div>
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/5 rounded-2xl shadow-2xl z-[150] overflow-hidden"
                    >
                        <div className="max-h-[300px] overflow-y-auto 
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-track]:bg-gray-100/50
          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-gray-300
          dark:[&::-webkit-scrollbar-track]:bg-white/5
          dark:[&::-webkit-scrollbar-thumb]:bg-white/10">
                            <div className="p-2 space-y-1">
                                {/* Option: All Members */}
                                <button
                                    type="button"
                                    onClick={() => handleSelect("")}
                                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all ${!selectedMember
                                        ? "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold"
                                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5"
                                        }`}
                                >
                                    <span>{placeholder}</span>
                                    {!selectedMember && <Check className="w-4 h-4" />}
                                </button>

                                {members?.length > 0 ? (
                                    members.map((member) => (
                                        <button
                                            key={member.Id}
                                            type="button"
                                            onClick={() => handleSelect(member.Id)}
                                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all ${String(selectedMember) === String(member.Id)
                                                ? "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold"
                                                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5"
                                                }`}
                                        >
                                            <span className="truncate">{member.MemberName}</span>
                                            {String(selectedMember) === String(member.Id) && <Check className="w-4 h-4" />}
                                        </button>
                                    ))
                                ) : (
                                    <div className="px-4 py-3 text-sm text-gray-400 text-center italic">
                                        No members available
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MemberSelect;
